import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { ArrowLeft, Tag, Plus, Trash2, Edit, Copy, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCoupons() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_percent: '',
    valid_for_plans: ['solo', 'crew'],
    max_uses: '',
    expires_at: '',
    description: '',
    duration_type: 'forever'
  });

  const durationOptions = [
    { value: '1_month', label: '1 Month Trial' },
    { value: '3_months', label: '3 Months Trial' },
    { value: '6_months', label: '6 Months Trial' },
    { value: '1_year', label: '1 Year Trial' },
    { value: 'forever', label: 'Forever (Permanent Discount)' }
  ];

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchCoupons = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/coupons`, {
        headers: getAuthHeaders()
      });
      setCoupons(response.data.coupons || []);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to fetch coupons');
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchCoupons();
  }, [navigate, fetchCoupons]);

  const resetForm = () => {
    setFormData({
      code: '',
      discount_percent: '',
      valid_for_plans: ['solo', 'crew'],
      max_uses: '',
      expires_at: '',
      description: '',
      duration_type: 'forever'
    });
    setEditingCoupon(null);
  };

  const handleCreateCoupon = async () => {
    if (!formData.code || !formData.discount_percent) {
      toast.error('Code and discount are required');
      return;
    }

    try {
      const payload = {
        code: formData.code,
        discount_percent: parseFloat(formData.discount_percent),
        valid_for_plans: formData.valid_for_plans,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expires_at: formData.expires_at || null,
        description: formData.description || null,
        duration_type: formData.duration_type || 'forever'
      };

      await axios.post(`${API}/admin/coupons`, payload, {
        headers: getAuthHeaders()
      });

      toast.success('Coupon created');
      setShowCreateModal(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const handleUpdateCoupon = async () => {
    if (!editingCoupon) return;

    try {
      await axios.put(
        `${API}/admin/coupons/${editingCoupon.id}`,
        {
          discount_percent: parseFloat(formData.discount_percent),
          valid_for_plans: formData.valid_for_plans,
          max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
          expires_at: formData.expires_at || null,
          description: formData.description || null,
          duration_type: formData.duration_type || 'forever'
        },
        { headers: getAuthHeaders() }
      );

      toast.success('Coupon updated');
      setShowCreateModal(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update coupon');
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await axios.put(
        `${API}/admin/coupons/${coupon.id}`,
        { is_active: !coupon.is_active },
        { headers: getAuthHeaders() }
      );
      fetchCoupons();
      toast.success(coupon.is_active ? 'Coupon deactivated' : 'Coupon activated');
    } catch (error) {
      toast.error('Failed to update coupon');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm('Delete this coupon?')) return;

    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      discount_percent: coupon.discount_percent.toString(),
      valid_for_plans: coupon.valid_for_plans,
      max_uses: coupon.max_uses?.toString() || '',
      expires_at: coupon.expires_at?.split('T')[0] || '',
      description: coupon.description || '',
      duration_type: coupon.duration_type || 'forever'
    });
    setShowCreateModal(true);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied');
  };

  const togglePlan = (plan) => {
    setFormData(prev => ({
      ...prev,
      valid_for_plans: prev.valid_for_plans.includes(plan)
        ? prev.valid_for_plans.filter(p => p !== plan)
        : [...prev.valid_for_plans, plan]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <meta name="robots" content="noindex, nofollow" />
      
      <div className="min-h-screen bg-slate-900">
        {/* Header */}
        <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/dashboard')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex items-center gap-2">
                <Tag className="w-6 h-6 text-red-500" />
                <h1 className="text-xl font-bold text-white">Coupon Codes</h1>
              </div>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Coupon
            </Button>
          </div>
        </header>

        <main className="p-6">
          <div className="max-w-4xl mx-auto">
            {coupons.length === 0 ? (
              <div className="bg-slate-800 rounded-xl border border-slate-700 p-12 text-center">
                <Tag className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">No Coupons Yet</h2>
                <p className="text-slate-400 mb-6">Create your first coupon code to offer discounts</p>
                <Button
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(true);
                  }}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Coupon
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {coupons.map(coupon => (
                  <div 
                    key={coupon.id}
                    className={`bg-slate-800 rounded-xl border ${coupon.is_active ? 'border-slate-700' : 'border-red-900 opacity-60'} p-6`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <button
                            onClick={() => copyCode(coupon.code)}
                            className="flex items-center gap-2 bg-slate-700 px-4 py-2 rounded-lg hover:bg-slate-600 transition-colors"
                          >
                            <span className="text-xl font-mono font-bold text-white">{coupon.code}</span>
                            <Copy className="w-4 h-4 text-slate-400" />
                          </button>
                          <span className="text-2xl font-bold text-emerald-400">
                            {coupon.discount_percent}% OFF
                          </span>
                          {!coupon.is_active && (
                            <span className="bg-red-900 text-red-300 text-xs font-bold px-2 py-1 rounded-full">
                              INACTIVE
                            </span>
                          )}
                        </div>
                        
                        <p className="text-slate-400 text-sm mb-3">
                          {coupon.description || 'No description'}
                        </p>

                        <div className="flex gap-4 text-sm flex-wrap">
                          <span className="text-slate-500">
                            Plans: {coupon.valid_for_plans.map(p => p === 'solo' ? 'Solo Pro' : 'Crew').join(', ')}
                          </span>
                          <span className="text-slate-500">
                            Uses: {coupon.current_uses}/{coupon.max_uses || '∞'}
                          </span>
                          <span className={`${coupon.duration_type === 'forever' ? 'text-emerald-400' : 'text-amber-400'}`}>
                            Duration: {coupon.duration_type === 'forever' ? 'Forever' : coupon.duration_type?.replace('_', ' ') || 'Forever'}
                          </span>
                          {coupon.expires_at && (
                            <span className="text-slate-500">
                              Expires: {new Date(coupon.expires_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(coupon)}
                          className={coupon.is_active ? 'text-amber-400' : 'text-emerald-400'}
                        >
                          {coupon.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(coupon)}
                          className="text-slate-400"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCoupon(coupon.id)}
                          className="text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </main>

        {/* Create/Edit Modal */}
        <Dialog open={showCreateModal} onOpenChange={(open) => {
          if (!open) resetForm();
          setShowCreateModal(open);
        }}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-400">Coupon Code</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="SUMMER20"
                  disabled={!!editingCoupon}
                  className="bg-slate-700 border-slate-600 font-mono uppercase"
                />
              </div>

              <div>
                <Label className="text-slate-400">Discount Percentage</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData(p => ({ ...p, discount_percent: e.target.value }))}
                  placeholder="20"
                  className="bg-slate-700 border-slate-600"
                />
              </div>

              <div>
                <Label className="text-slate-400">Discount Duration</Label>
                <select
                  value={formData.duration_type}
                  onChange={(e) => setFormData(p => ({ ...p, duration_type: e.target.value }))}
                  className="w-full mt-1 bg-slate-700 border border-slate-600 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {durationOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  {formData.duration_type === 'forever' 
                    ? 'Discount applies permanently to the subscription'
                    : `Discount applies for the first ${formData.duration_type.replace('_', ' ')}, then reverts to full price`
                  }
                </p>
              </div>

              <div>
                <Label className="text-slate-400">Valid For Plans</Label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.valid_for_plans.includes('solo')}
                      onCheckedChange={() => togglePlan('solo')}
                    />
                    <span className="text-slate-300">Solo Pro</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.valid_for_plans.includes('crew')}
                      onCheckedChange={() => togglePlan('crew')}
                    />
                    <span className="text-slate-300">Crew</span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-400">Max Uses (optional)</Label>
                  <Input
                    type="number"
                    value={formData.max_uses}
                    onChange={(e) => setFormData(p => ({ ...p, max_uses: e.target.value }))}
                    placeholder="Unlimited"
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
                <div>
                  <Label className="text-slate-400">Expires (optional)</Label>
                  <Input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => setFormData(p => ({ ...p, expires_at: e.target.value }))}
                    className="bg-slate-700 border-slate-600"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-400">Description (optional)</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                  placeholder="Summer promotion discount"
                  className="bg-slate-700 border-slate-600"
                />
              </div>

              <Button
                onClick={editingCoupon ? handleUpdateCoupon : handleCreateCoupon}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
